import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { XCircle, Compass } from "lucide-react";
import { toast } from "sonner";
import { trackCompleteRegistration } from "@/lib/metaPixel";

// After the email link is clicked, the backend auto-logs the user in (sets auth
// cookies) and returns their role-specific setup path. We then refresh the auth
// context and forward straight to that page — no sign-in prompt in-between.
// Second-click fallback: the backend 400s an already-used or expired token.
// Rather than show a raw error, we send the user to /login with a friendly
// toast so they can just sign in — their email is already verified by then.
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", message: "" });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) {
      setState({ status: "error", message: "Missing verification token." });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        const fresh = await refresh();
        trackCompleteRegistration(fresh?.role);
        navigate(data.next_path || "/profile-setup", { replace: true });
      } catch (e) {
        // Backend returns 400 for "already used" or "expired" verification links.
        // In both cases the email itself is already verified on the user doc, so
        // redirect to Sign-In with a user-friendly toast instead of a raw error.
        const status = e?.response?.status;
        if (status === 400) {
          toast.info("Your email is already verified. Please sign in.");
          navigate("/login", { replace: true });
          return;
        }
        setState({ status: "error", message: errMsg(e, "Verification failed.") });
      }
    })();
  }, [params, refresh, navigate]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 grain" data-testid="verify-email-page">
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-10 max-w-md w-full text-center shadow-sm">
        {state.status === "loading" && (
          <>
            <Compass className="w-12 h-12 text-sage animate-spin mx-auto mb-4" style={{ animationDuration: "2s" }} />
            <h1 className="text-xl font-black text-forest mb-1">Confirming your email…</h1>
            <p className="text-forest/60 text-sm" data-testid="verify-loading-msg">Taking you to your profile setup — one second.</p>
          </>
        )}
        {state.status === "error" && (
          <>
            <span className="inline-flex w-16 h-16 rounded-full bg-terracotta-light items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-terracotta-dark" />
            </span>
            <h1 className="text-2xl font-black text-forest mb-2">Link problem</h1>
            <p className="text-forest/70 text-sm mb-6" data-testid="verify-error-msg">{state.message}</p>
            <Link to="/login">
              <Button variant="outline" className="rounded-full w-full" data-testid="verify-back-btn">Back to sign in</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
